import React, { useState, useEffect } from "react";
import { IoCloseSharp } from "react-icons/io5";
import { FiStar, FiMessageSquare, FiUser } from "react-icons/fi";
import Button from "../../../components/ui/Button";
import FileUpload from "../../../components/ui/FileUpload";
import Input from "../../../components/ui/Input";
import { strings } from "../../../strings/strings";
import { apiCall } from "../../../api/fetchClient";
import { ADD_REVIEW_URL, UPDATE_REVIEW_URL } from "../../../api/url";

const ReviewModal = ({ isOpen, onClose, onSave, review }) => {
  const [formData, setFormData] = useState({
    name: "",
    designation: "",
    companyName: "",
    reviewNotes: "",
    rating: 0,
    status: "Active",
    displayOrder: "",
    companyLogo: null,
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (review) {
        setFormData({
          name: review.name || "",
          designation: review.designation || "",
          companyName: review.companyName || "",
          reviewNotes: review.reviewNotes || "",
          rating: review.rating || 0,
          status: review.isActive !== undefined ? (review.isActive ? "Active" : "Inactive") : (review.status || "Active"),
          displayOrder: review.displayOrder || "",
          companyLogo: review.companyLogo || null,
        });
      } else {
        setFormData({
          name: "",
          designation: "",
          companyName: "",
          reviewNotes: "",
          rating: 0,
          status: "Active",
          displayOrder: "",
          companyLogo: null,
        });
      }
      setErrors({});
      setIsSubmitting(false);
    }
  }, [isOpen, review]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleRatingChange = (ratingValue) => {
    setFormData(prev => ({ ...prev, rating: ratingValue }));
    if (errors.rating) {
      setErrors(prev => ({ ...prev, rating: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Reviewer name is required";
    if (!formData.companyName.trim()) newErrors.companyName = "Company name is required";
    if (!formData.reviewNotes.trim()) newErrors.reviewNotes = "Review notes are required";
    if (!review && !formData.companyLogo) newErrors.companyLogo = "Company logo is required";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      const firstErrorField = Object.keys(errors)[0];
      const element = document.getElementById(firstErrorField);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
      }
      return;
    }
    
    try {
      setIsSubmitting(true);
      const dataToSend = new FormData();

      if (formData.name) dataToSend.append("name", formData.name);
      if (formData.designation) dataToSend.append("designation", formData.designation);
      if (formData.companyName) dataToSend.append("companyName", formData.companyName);
      if (formData.reviewNotes) dataToSend.append("reviewNotes", formData.reviewNotes);
      if (formData.rating) dataToSend.append("rating", formData.rating);
      if (formData.status) dataToSend.append("isActive", formData.status === "Active");
      if (formData.displayOrder) dataToSend.append("displayOrder", formData.displayOrder);

      if (formData.companyLogo instanceof Blob || formData.companyLogo instanceof File) {
        dataToSend.append("companyLogo", formData.companyLogo, formData.companyLogo.name || 'logo.png');
      }

      let response;
      if (review) {
        response = await apiCall(`${UPDATE_REVIEW_URL}${review._id || review.id}`, 'PUT', dataToSend, true);
        if (response?.success) {
        }
      } else {
        response = await apiCall(ADD_REVIEW_URL, 'POST', dataToSend, true);
        if (response?.success) {
        }
      }

      onSave(response?.results || formData);
    } catch (error) {

    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm transition-opacity" onClick={onClose} />
      
      <div className="relative bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] flex flex-col border border-gray-100 dark:border-slate-800">
        
        <div className="flex items-center justify-between p-5 sm:p-6 border-b border-gray-100 dark:border-slate-800 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <FiStar className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                {review ? strings.EDIT_REVIEW : strings.ADD_REVIEW}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {review ? "Update the details for this review" : "Enter details to create a new review"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors p-2 hover:bg-gray-50 dark:hover:bg-slate-800 rounded-lg"
          >
            <IoCloseSharp className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 p-5 sm:p-6 space-y-8" id="review-form" noValidate>
          
          <section>
            <div className="flex items-center gap-2 mb-4">
              <FiMessageSquare className="w-4 h-4 text-gray-400" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Review Details</h3>
            </div>
            
            <div className="bg-gray-50 dark:bg-slate-800/50 rounded-xl p-5 border border-gray-100 dark:border-slate-700 space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {strings.RATING}
                </label>
                <div className="flex gap-1.5 w-max -ml-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => handleRatingChange(star)}
                      className={`p-1 transition-transform hover:scale-110 ${
                        formData.rating >= star 
                          ? "text-yellow-400" 
                          : "text-gray-300 dark:text-slate-500 hover:text-yellow-200"
                      }`}
                    >
                      <FiStar className={`w-6 h-6 ${formData.rating >= star ? "fill-current" : ""}`} />
                    </button>
                  ))}
                </div>
                {errors.rating && <p className="mt-1.5 text-xs text-red-500">{errors.rating}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {strings.REVIEW_CONTENT} <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="reviewNotes"
                  name="reviewNotes"
                  value={formData.reviewNotes}
                  onChange={handleChange}
                  placeholder={strings.REVIEW_CONTENT_PLACEHOLDER}
                  rows={4}
                  className={`w-full px-4 py-3 text-sm border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-slate-900 dark:text-white resize-none shadow-sm transition-all ${errors.reviewNotes ? 'border-red-500' : 'border-gray-200 dark:border-slate-700'}`}
                />
                {errors.reviewNotes && <p className="mt-1.5 text-xs text-red-500">{errors.reviewNotes}</p>}
              </div>
            </div>
          </section>

          <section>
            <div className="flex items-center gap-2 mb-4">
              <FiUser className="w-4 h-4 text-gray-400" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Reviewer Information</h3>
            </div>
            
            <div className="bg-gray-50 dark:bg-slate-800/50 rounded-xl p-5 border border-gray-100 dark:border-slate-700">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="sm:col-span-2">
                  <FileUpload
                    id="companyLogo"
                    label={strings.COMPANY_LOGO}
                    name="companyLogo"
                    value={formData.companyLogo}
                    onChange={handleChange}
                    error={errors.companyLogo}
                  />
                </div>
                
                <Input
                  id="name"
                  name="name"
                  label={strings.REVIEWER_NAME}
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={strings.REVIEWER_NAME_PLACEHOLDER}
                  error={errors.name}
                  required
                />
                
                <Input
                  id="designation"
                  name="designation"
                  label={strings.DESIGNATION}
                  value={formData.designation}
                  onChange={handleChange}
                  placeholder={strings.DESIGNATION_PLACEHOLDER}
                  error={errors.designation}
                />

                <Input
                  id="companyName"
                  name="companyName"
                  label={strings.COMPANY_NAME_LABEL}
                  value={formData.companyName}
                  onChange={handleChange}
                  placeholder={strings.COMPANY_NAME_PLACEHOLDER || 'Enter company name'}
                  error={errors.companyName}
                  required
                />
                
                {review && (
                  <>
                    <Input
                      id="displayOrder"
                      name="displayOrder"
                      label="Display Order"
                      type="number"
                      value={formData.displayOrder}
                      onChange={handleChange}
                      placeholder="Enter display order"
                      error={errors.displayOrder}
                    />

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {strings.STATUS}
                      </label>
                      <select
                        id="status"
                        name="status"
                        value={formData.status}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 text-sm rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent transition-all outline-none shadow-sm"
                      >
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </select>
                    </div>
                  </>
                )}
              </div>
            </div>
          </section>
          
        </form>

        <div className="flex items-center justify-end gap-3 p-5 sm:px-6 border-t border-gray-100 dark:border-slate-800 bg-gray-50/50 dark:bg-slate-900 shrink-0">
          <Button type="button" variant="secondary" onClick={onClose} className="px-6">
            {strings.CANCEL}
          </Button>
          <Button type="submit" form="review-form" variant="primary" className="px-6" disabled={isSubmitting}>
            {isSubmitting ? (review ? "Updating..." : "Adding...") : (review ? strings.SAVE_CHANGES : strings.ADD_REVIEW)}
          </Button>
        </div>

      </div>
    </div>
  );
};

export default ReviewModal;
